import { STAFF_DUTY_LABELS, type Shift, type ShiftBoardEntry } from "@station/contracts";
import { Alert, Button, Card, EmptyState, MoneyText, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Banknote, ClipboardList, Play, Receipt, Square } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { StartClosingDialog } from "../../../features/shifts/closing-dialogs.js";
import { closingTotalsOf } from "../../../features/shifts/closing-values.js";
import { OpenShiftDialog } from "../../../features/shifts/open-shift-dialog.js";
import {
  useShift,
  useShiftBoard,
  useShiftClosings,
  useShiftPermissions,
} from "../../../features/shifts/queries.js";
import { CashDropDialog, CreditSaleDialog } from "../../../features/shifts/record-dialogs.js";
import {
  Field,
  NoStations,
  ShiftStationField,
  ShiftStatusBadge,
  ShiftsPage,
  shiftsSearchShape,
  useShiftStation,
} from "../../../features/shifts/shared.js";
import { formatElapsed } from "../../../features/shifts/shift-values.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object({ ...shiftsSearchShape });

export const Route = createFileRoute("/_app/vardiya/pano")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ShiftBoardPage,
});

/**
 * Vardiya panosu — modülün açılış sayfası: AÇIK ve kapanışı süren vardiyalar, kimin hangi ada / pompa /
 * tabancalarda ve hangi kasada olduğu, devir parası ve ara teslim toplamı.
 *
 * `trackedCash` canlı satış TAHMİNİ DEĞİLDİR (devir parası + ara teslimler); beklenen tutar kapanışın işidir.
 * Pano 30 saniyede bir tazelenir — canlı sayaç yoktur, süre "3 sa 12 dk" gibi yuvarlanır.
 */
function ShiftBoardPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  const [opening, setOpening] = useState(false);

  return (
    <ShiftsPage
      title={t("menu.shiftBoard")}
      description={t("shifts.board.description")}
      breadcrumb={[{ label: t("menu.shiftBoard") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <BoardBody companyId={companyId} opening={opening} setOpening={setOpening} />
        ) : (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        )
      }
    </ShiftsPage>
  );
}

function BoardBody({
  companyId,
  opening,
  setOpening,
}: {
  companyId: string;
  opening: boolean;
  setOpening: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  const station = useShiftStation(companyId);
  const board = useShiftBoard(companyId, station.stationId);

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end justify-between gap-3 p-4">
        <ShiftStationField selection={station} />
        {permissions.canManage ? (
          <Button onClick={() => setOpening(true)}>
            <Play aria-hidden="true" />
            {t("shifts.open.title")}
          </Button>
        ) : null}
      </Card>

      {board.error ? (
        <Alert tone="danger">{errorMessage(board.error)}</Alert>
      ) : board.isPending ? (
        <SkeletonRows rows={4} />
      ) : board.data.items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<ClipboardList />}
          title={t("shifts.board.emptyTitle")}
          description={t("shifts.board.emptyDescription")}
          action={
            permissions.canManage ? (
              <Button onClick={() => setOpening(true)}>
                <Play aria-hidden="true" />
                {t("shifts.open.title")}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className="grid gap-4 lg:grid-cols-2">
          {board.data.items.map((entry) => (
            <li key={entry.shiftId}>
              <BoardCard
                entry={entry}
                canManage={permissions.canManage}
                canManageClosing={permissions.canManageClosing}
              />
            </li>
          ))}
        </ul>
      )}

      <OpenShiftDialog
        open={opening}
        onOpenChange={setOpening}
        companyId={companyId}
        stations={station.stations}
        defaultStationId={station.stationId}
      />
    </>
  );
}

function BoardCard({
  entry,
  canManage,
  canManageClosing,
}: {
  entry: ShiftBoardEntry;
  canManage: boolean;
  canManageClosing: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<"cashDrop" | "creditSale" | "startClosing" | null>(null);
  // Hızlı eylemler ayrıntıdaki pencerelerin AYNISIDIR: üye listesi (ve kapanışta atamalar) için ayrıntı gerekir.
  const shift = useShift(dialog === null ? null : entry.shiftId);
  // Kapanışı SÜREN vardiyada ilerleme gösterilir: uç, satırı olmayan üyeler için hesap satırını da açar.
  const closings = useShiftClosings(entry.status === "CLOSING" ? entry.shiftId : null);
  const progress = closingTotalsOf(closings.data ?? []);

  return (
    <Card className="grid gap-3 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="grid gap-1">
          <Link
            to="/vardiya/vardiyalar/$id"
            params={{ id: entry.shiftId }}
            className="font-mono text-base font-semibold underline underline-offset-4"
          >
            {entry.shiftNo}
          </Link>
          <span className="text-sm text-muted-foreground">
            {entry.name} · {entry.stationName}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {entry.status === "CLOSING" && closings.data ? (
            <StatusBadge tone={progress.pending === 0 ? "success" : "warning"}>
              {t("shifts.closing.progress", {
                accepted: progress.accepted,
                total: progress.accepted + progress.pending,
              })}
            </StatusBadge>
          ) : null}
          <ShiftStatusBadge status={entry.status} />
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label={t("shifts.fields.businessDate")}>{formatDate(entry.businessDate)}</Field>
        <Field label={t("shifts.fields.openedAt")}>{formatDateTime(entry.openedAt)}</Field>
        <Field label={t("shifts.board.elapsed")}>{formatElapsed(entry.openedAt)}</Field>
        <Field label={t("shifts.fields.plannedEndAt")}>
          {entry.plannedEndAt ? formatDateTime(entry.plannedEndAt) : "—"}
        </Field>
      </dl>

      <ul className="grid gap-2">
        {entry.members.map((member) => (
          <li key={member.memberId} className="rounded-md border p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium">
                {member.staffCode} — {member.staffName}
              </span>
              <span className="text-xs text-muted-foreground">{STAFF_DUTY_LABELS[member.duty]}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {member.nozzleLabels.length > 0 ? member.nozzleLabels.join(", ") : t("shifts.board.noNozzles")}
              {member.cashRegisterName ? ` · ${member.cashRegisterName}` : ""}
            </p>
            <p className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span className="flex items-baseline gap-1.5">
                <span className="text-xs text-muted-foreground">{t("shifts.fields.openingFloat")}</span>
                <MoneyText value={member.openingFloat} />
              </span>
              <span className="flex items-baseline gap-1.5">
                <span className="text-xs text-muted-foreground">{t("shifts.fields.cashDropTotal")}</span>
                <MoneyText value={member.cashDropTotal} />
              </span>
              <span className="flex items-baseline gap-1.5">
                <span className="text-xs text-muted-foreground">{t("shifts.board.trackedCash")}</span>
                <MoneyText value={member.trackedCash} className="font-semibold" />
              </span>
            </p>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2">
        {canManage ? (
          <>
            <Button variant="outline" size="sm" onClick={() => setDialog("cashDrop")}>
              <Banknote aria-hidden="true" />
              {t("shifts.records.cashDropTitle")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDialog("creditSale")}>
              <Receipt aria-hidden="true" />
              {t("shifts.records.creditSaleTitle")}
            </Button>
          </>
        ) : null}
        {entry.status === "OPEN" && canManageClosing ? (
          <Button size="sm" onClick={() => setDialog("startClosing")}>
            <Square aria-hidden="true" />
            {t("shifts.closing.startTitle")}
          </Button>
        ) : null}
        {entry.status === "CLOSING" ? (
          <Button variant="outline" size="sm" asChild>
            <Link to="/vardiya/vardiyalar/$id" params={{ id: entry.shiftId }} search={{ tab: "closings" }}>
              {t("shifts.detail.tab.closings")}
            </Link>
          </Button>
        ) : null}
      </div>

      {shift.data ? (
        <>
          <StartClosingDialog
            open={dialog === "startClosing"}
            onOpenChange={(next) => setDialog(next ? "startClosing" : null)}
            shift={shift.data as Shift}
          />
          <CashDropDialog
            open={dialog === "cashDrop"}
            onOpenChange={(next) => setDialog(next ? "cashDrop" : null)}
            shiftId={entry.shiftId}
            members={shift.data.members}
          />
          <CreditSaleDialog
            open={dialog === "creditSale"}
            onOpenChange={(next) => setDialog(next ? "creditSale" : null)}
            shiftId={entry.shiftId}
            members={shift.data.members}
            stationId={shift.data.stationId}
          />
        </>
      ) : null}
    </Card>
  );
}
